import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { Chart, registerables } from 'chart.js';
import { StatisticsService } from '../../services/statistics.service';

Chart.register(...registerables);

@Component({
  standalone: false,
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css'],
})
export class HomeComponent implements OnInit, OnDestroy {
  @ViewChild('orderChart')    orderChartRef!:    ElementRef<HTMLCanvasElement>;
  @ViewChild('inventoryChart') inventoryChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('monthlyChart')  monthlyChartRef!:  ElementRef<HTMLCanvasElement>;

  stats: any        = null;
  loading           = true;
  monthlyStats: any[] = [];
  totalMonthlyOrders  = 0;
  monthlyLoading      = true;

  get skeletonCards() { return Array(this.isSuperAdmin ? 4 : 3); }

  get isSuperAdmin(): boolean {
    return localStorage.getItem('is_super_admin') === 'true';
  }

  get hasInventoryData(): boolean {
    return this.stats && (
      this.stats.total_products > 0 ||
      this.stats.total_categories > 0 ||
      this.stats.total_packages > 0 ||
      this.stats.total_blogs > 0
    );
  }

  private orderChart?:    Chart;
  private inventoryChart?: Chart;
  private monthlyChart?:  Chart;

  constructor(private statisticsService: StatisticsService) {}

  ngOnInit() {
    this.statisticsService.getStatistics().subscribe({
      next: (data: any) => {
        this.stats = data;
        this.loading = false;
        setTimeout(() => this.initCharts(), 0);
      },
      error: () => { this.loading = false; },
    });

    this.statisticsService.getMonthlyOrders().subscribe({
      next: (data: any) => {
        this.monthlyStats       = data.stats || [];
        this.totalMonthlyOrders = data.total_orders || 0;
        this.monthlyLoading     = false;
        // Only init chart if main stats are already loaded (outer *ngIf is true, canvas is in DOM)
        setTimeout(() => {
          if (!this.loading && this.stats) this.initMonthlyChart();
        }, 0);
      },
      error: () => { this.monthlyLoading = false; },
    });
  }

  private sharedTooltip() {
    return {
      backgroundColor: 'rgba(15,23,42,0.92)',
      titleColor:  '#F1F5F9',
      bodyColor:   '#94A3B8',
      borderColor: 'rgba(255,255,255,0.08)',
      borderWidth:  1,
      padding:      12,
      cornerRadius: 10,
      displayColors: false,
    };
  }

  private initCharts(): void {
    const tickColor = '#94A3B8';
    const gridColor = 'rgba(148,163,184,0.1)';
    const bgCard = getComputedStyle(document.documentElement)
      .getPropertyValue('--bg-card').trim() || '#ffffff';

    const COLORS = {
      pending:   '#FBBF24',
      delivered: '#34D399',
      cancelled: '#FB7185',
    };

    this.orderChart = new Chart(this.orderChartRef.nativeElement, {
      type: 'doughnut',
      data: {
        labels: ['Pending', 'Delivered', 'Cancelled'],
        datasets: [{
          data: [
            this.stats.pending_orders,
            this.stats.delivered_orders,
            this.stats.cancelled_orders,
          ],
          backgroundColor:      [COLORS.pending, COLORS.delivered, COLORS.cancelled],
          hoverBackgroundColor: [COLORS.pending, COLORS.delivered, COLORS.cancelled],
          borderColor:      bgCard,
          hoverBorderColor: bgCard,
          borderWidth:   4,
          borderRadius:  6,
          hoverOffset:  10,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '76%',
        animation: { duration: 700, easing: 'easeInOutQuart' },
        plugins: {
          legend: { display: false },
          tooltip: {
            ...this.sharedTooltip(),
            callbacks: { label: (c) => `  ${c.label}  ·  ${c.parsed}` },
          },
        },
      },
    });

    // If monthly data already arrived before stats, init its chart now (canvas is in DOM)
    if (!this.monthlyLoading && this.monthlyStats.length > 0 && !this.monthlyChart) {
      this.initMonthlyChart();
    }

    if (!this.inventoryChartRef) return;

    this.inventoryChart = new Chart(this.inventoryChartRef.nativeElement, {
      type: 'bar',
      data: {
        labels: ['Products', 'Categories', 'Packages', 'Blogs'],
        datasets: [{
          label: 'Count',
          data: [
            this.stats.total_products,
            this.stats.total_categories,
            this.stats.total_packages,
            this.stats.total_blogs,
          ],
          backgroundColor: ['#818CF8', '#FBBF24', '#34D399', '#38BDF8'],
          borderRadius: 10,
          borderSkipped: false,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y',
        animation: { duration: 700, easing: 'easeInOutQuart' },
        plugins: {
          legend: { display: false },
          tooltip: {
            ...this.sharedTooltip(),
            callbacks: { label: (c) => `  ${c.label}  ·  ${c.parsed}` },
          },
        },
        scales: {
          x: {
            grid:   { color: gridColor },
            ticks:  { color: tickColor, font: { family: 'Inter, Roboto, sans-serif', size: 12 } },
            border: { display: false },
          },
          y: {
            grid:   { display: false },
            ticks:  { color: tickColor, font: { family: 'Inter, Roboto, sans-serif', size: 13 }, padding: 8 },
            border: { display: false },
          },
        },
      },
    });
  }

  private initMonthlyChart(): void {
    if (!this.monthlyChartRef || this.monthlyStats.length === 0) return;

    const tickColor = '#94A3B8';
    const bgCard = getComputedStyle(document.documentElement)
      .getPropertyValue('--bg-card').trim() || '#ffffff';

    const monthlyData = this.monthlyStats;

    this.monthlyChart = new Chart(this.monthlyChartRef.nativeElement, {
      type: 'line',
      data: {
        labels: monthlyData.map((s: any) => s.label),
        datasets: [{
          label: 'Orders',
          data:  monthlyData.map((s: any) => s.count),
          borderColor:     '#6366F1',
          backgroundColor: (context: any) => {
            const { ctx, chartArea } = context.chart;
            if (!chartArea) return 'rgba(99,102,241,0.15)';
            const g = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
            g.addColorStop(0, 'rgba(99,102,241,0.28)');
            g.addColorStop(1, 'rgba(99,102,241,0.00)');
            return g;
          },
          fill:        true,
          tension:     0.42,
          borderWidth: 2.5,
          pointBackgroundColor:  '#6366F1',
          pointBorderColor:      bgCard,
          pointBorderWidth:      2.5,
          pointRadius:           5,
          pointHoverRadius:      7,
          pointHoverBackgroundColor: '#6366F1',
          pointHoverBorderColor:     '#fff',
        }],
      },
      options: {
        responsive:          true,
        maintainAspectRatio: false,
        animation: { duration: 900, easing: 'easeInOutQuart' },
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            ...this.sharedTooltip(),
            callbacks: {
              title:       (items: any[]) => items[0]?.label ?? '',
              label:       (c: any)       => `  ${c.parsed.y} orders`,
              afterLabel:  (c: any)       => {
                const rev = monthlyData[c.dataIndex]?.revenue;
                return rev ? `  ${Number(rev).toLocaleString()} EGP revenue` : '';
              },
            },
          },
        },
        scales: {
          x: {
            grid:   { display: false },
            border: { display: false },
            ticks:  { color: tickColor, font: { family: 'Inter, Roboto, sans-serif', size: 12 } },
          },
          y: {
            beginAtZero: true,
            grid:   { color: 'rgba(148,163,184,0.10)' },
            border: { display: false },
            ticks:  {
              color: tickColor,
              font:  { family: 'Inter, Roboto, sans-serif', size: 12 },
              precision: 0,
              stepSize:  1,
            },
          },
        },
      },
    });
  }

  ngOnDestroy(): void {
    this.orderChart?.destroy();
    this.inventoryChart?.destroy();
    this.monthlyChart?.destroy();
  }
}
